import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { OWNER_CONSOLE_DISPLAY_LABEL } from './ownerConsoleDisplayMeta';

const DEFAULT_DESCRIPTION = 'ベット参加して、展開を予想しよう';

const buildParticipationUrl = (roomId) => {
  if (!roomId) {
    return '';
  }

  if (typeof window === 'undefined' || !window.location?.origin) {
    return `/room?id=${encodeURIComponent(roomId)}`;
  }

  return `${window.location.origin}/room?id=${encodeURIComponent(roomId)}`;
};

const formatPoint = (value) => `${Number(value || 0).toLocaleString()} pt`;

const getParticipantsCount = (liveSummary, roundSummary) => {
  const participantsCount = liveSummary?.participantsCount ?? roundSummary?.participantsCount ?? 0;

  return Number(participantsCount);
};

const getRoomTotalBet = (event) => Number(
  event?.total_bet ??
  event?.totalBet ??
  0
);

function ParticipationQr({ participationUrl }) {
  const qrImageUrl = participationUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=144x144&margin=8&data=${encodeURIComponent(participationUrl)}`
    : '';

  return (
    <Box
      sx={{
        width: { sm: 96, md: 112 },
        height: { sm: 96, md: 112 },
        flex: '0 0 auto',
        borderRadius: 2,
        bgcolor: '#fff',
        color: '#0f172a',
        display: 'grid',
        placeItems: 'center',
        p: 1,
        boxShadow: '0 10px 28px rgba(0,0,0,0.26)',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
      aria-label={participationUrl ? '参加URLのQRコード' : '参加URL取得中'}
    >
      {qrImageUrl ? (
        <Box
          component="img"
          src={qrImageUrl}
          alt="参加URL QRコード"
          sx={{ width: '100%', height: '100%', display: 'block' }}
        />
      ) : (
        <Typography variant="caption" sx={{ fontWeight: 900, textAlign: 'center', lineHeight: 1.35 }}>
          QR準備中
        </Typography>
      )}
    </Box>
  );
}

function RoomOverview({ event, phaseState, round, title, description, liveSummary, roundSummary }) {
  const phaseLabel = OWNER_CONSOLE_DISPLAY_LABEL[phaseState?.displayCategory] || phaseState?.fallbackMessage || '状態取得中';
  const roundLabel = `ROUND ${round?.number || event?.round || '-'}`;
  const participantsCount = getParticipantsCount(liveSummary, roundSummary);
  const roomTotalBet = getRoomTotalBet(event);

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        component="h1"
        sx={{
          m: 0,
          color: '#fff',
          fontSize: { sm: 22, md: 28 },
          fontWeight: 950,
          lineHeight: 1.12,
          overflowWrap: 'anywhere',
          whiteSpace: 'normal',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(226,232,240,0.82)',
          fontSize: { sm: 12, md: 13 },
          lineHeight: 1.35,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          maxWidth: '100%',
        }}
        title={description}
      >
        {description}
      </Typography>
      <Box
        sx={{
          width: 'min(360px, 80%)',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          my: 0.75,
        }}
      />
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            color: '#e0f2fe',
            fontSize: { sm: 17, md: 20 },
            fontWeight: 950,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
          }}
        >
          {roundLabel}
        </Typography>
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.35,
            borderRadius: '999px',
            color: '#bae6fd',
            bgcolor: 'rgba(14, 165, 233, 0.14)',
            border: '1px solid rgba(125, 211, 252, 0.26)',
            fontSize: { sm: 11, md: 12 },
            fontWeight: 900,
            lineHeight: 1.2,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {phaseLabel}
        </Box>
      </Stack>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(226,232,240,0.74)',
          fontWeight: 800,
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
        }}
      >
        参加者 {participantsCount}人 ｜ ルーム総ベット額 {formatPoint(roomTotalBet)}
      </Typography>
    </Stack>
  );
}

function ParticipationGuide({ participationUrl }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        borderLeft: '1px solid rgba(148, 163, 184, 0.22)',
        pl: { sm: 1, md: 1.25 },
        display: 'grid',
        gridTemplateColumns: { sm: '96px minmax(0, 1fr)', md: '112px minmax(0, 1fr)' },
        gap: { sm: 1, md: 1.25 },
        alignItems: 'center',
      }}
    >
      <ParticipationQr participationUrl={participationUrl} />
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: '#fff',
            fontSize: { sm: 15, md: 17 },
            fontWeight: 950,
            lineHeight: 1.15,
          }}
        >
          参加
        </Typography>
        <Typography
          sx={{
            color: '#bae6fd',
            fontSize: { sm: 12, md: 14 },
            fontWeight: 900,
            lineHeight: 1.2,
            overflowWrap: 'anywhere',
          }}
        >
          betty-room.com
        </Typography>
      </Stack>
    </Box>
  );
}

export default function OwnerConsoleStaticHeader({ event, roomId, round, phaseState, liveSummary, roundSummary }) {
  const participationRoomId = event?.room_id || roomId;
  const participationUrl = useMemo(() => buildParticipationUrl(participationRoomId), [participationRoomId]);
  const description = event?.description || DEFAULT_DESCRIPTION;
  const title = event?.name || 'Betty Live View';

  return (
    <Box
      component="header"
      sx={{
        gridArea: 'header',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) clamp(260px, 32%, 360px)',
        gap: { sm: 1, md: 1.25 },
        alignItems: 'center',
        px: { sm: 1.5, md: 2 },
        py: { sm: 1, md: 1.25 },
        pl: { sm: '180px', md: '180px' },
        borderRadius: 3,
        bgcolor: 'rgba(15, 23, 42, 0.86)',
        border: '1px solid rgba(148, 163, 184, 0.24)',
        color: '#fff',
        minWidth: 0,
      }}
    >
      <RoomOverview
        event={event}
        phaseState={phaseState}
        round={round}
        title={title}
        description={description}
        liveSummary={liveSummary}
        roundSummary={roundSummary}
      />
      <ParticipationGuide participationUrl={participationUrl} />
    </Box>
  );
}
